/**
 * Delete Leads Modal — two-step confirmation
 * Allows clients to delete pending leads (all or by run).
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  collection, query, where, orderBy, limit,
  getDocs, getCountFromServer,
} from 'firebase/firestore';
import { db, functions } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';
import { notifyLeadsChanged } from '../../hooks/leadRunCounts';

interface RunCount {
  pending: number;
}

interface DeleteLeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  runCounts: Record<number, RunCount>;
  clientId?: string | null; // Admin impersonation
}

export function DeleteLeadsModal({ isOpen, onClose, runCounts, clientId }: DeleteLeadsModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<'choose' | 'pickFile' | 'confirm' | 'progress' | 'done'>('choose');
  const [mode, setMode] = useState<'all' | 'run' | 'file'>('all');
  const [selectedRun, setSelectedRun] = useState<number>(1);
  const [selectedSourceFile, setSelectedSourceFile] = useState<string>('');
  const [fileOptions, setFileOptions] = useState<{ sourceFile: string; pendingCount: number }[] | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  // Resolves which client document the modal is operating against. Admin
  // impersonation passes a clientId prop; otherwise we use the logged-in user.
  const targetClientId = clientId || user?.uid || '';

  const totalPending = Object.values(runCounts).reduce((sum, r) => sum + (r?.pending || 0), 0);
  const fileTargetCount = fileOptions?.find(f => f.sourceFile === selectedSourceFile)?.pendingCount || 0;
  const targetCount = mode === 'all' ? totalPending
    : mode === 'run' ? (runCounts[selectedRun]?.pending || 0)
    : fileTargetCount;
  const targetLabel = mode === 'all' ? 'all runs'
    : mode === 'run' ? `Run ${selectedRun}`
    : `upload "${selectedSourceFile}"`;

  const handleClose = () => {
    setStep('choose');
    setMode('all');
    setSelectedRun(1);
    setSelectedSourceFile('');
    setFileOptions(null);
    setFilesLoading(false);
    setConfirmText('');
    setResult(null);
    setError(null);
    onClose();
  };

  // Reads recent uploadEvents to discover labelled uploads, then runs one
  // count() aggregation per upload to show pending lead counts. Cheap because
  // count() is server-side. Capped at 50 most recent uploads — older ones
  // remain deletable via 'all' or 'run' modes.
  const loadFileOptions = async () => {
    if (!targetClientId) return;
    setFilesLoading(true);
    setError(null);
    try {
      const eventsSnap = await getDocs(query(
        collection(db, 'clients', targetClientId, 'uploadEvents'),
        orderBy('uploadedAt', 'desc'),
        limit(50),
      ));
      const seen = new Set<string>();
      const candidates: string[] = [];
      eventsSnap.forEach(d => {
        const sf = d.data().sourceFile as string | undefined;
        if (sf && !seen.has(sf)) { seen.add(sf); candidates.push(sf); }
      });
      const counts = await Promise.all(candidates.map(async sf => {
        const cs = await getCountFromServer(query(
          collection(db, 'clients', targetClientId, 'leads'),
          where('status', '==', 'pending'),
          where('sourceFile', '==', sf),
        ));
        return { sourceFile: sf, pendingCount: cs.data().count };
      }));
      setFileOptions(counts.filter(c => c.pendingCount > 0));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load uploads: ${msg}`);
      setFileOptions([]);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleConfirm = async () => {
    setStep('progress');
    setError(null);
    try {
      const deleteLeads = httpsCallable(functions, 'deleteLeads', { timeout: 300000 });
      const response = await deleteLeads({
        mode,
        ...(mode === 'run' ? { runNumber: selectedRun } : {}),
        ...(mode === 'file' ? { sourceFile: selectedSourceFile } : {}),
        ...(clientId ? { clientId } : {}),
      });
      const data = response.data as { message: string; deletedCount: number };
      setResult(data.message);
      setStep('done');
      notifyLeadsChanged();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      // If timeout but deletion is still running server-side, show a softer message
      if (message.includes('deadline-exceeded') || message.includes('DEADLINE_EXCEEDED')) {
        setResult(`Deletion is still running in the background. The run grid will update as leads are removed.`);
        setStep('done');
        return;
      }
      setError(message);
      setStep('confirm');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={handleClose}>
      <div style={{
        background: 'var(--dark-2)', borderRadius: '12px', padding: '32px',
        maxWidth: '480px', width: '90%', border: '1px solid var(--dark-3)',
      }} onClick={e => e.stopPropagation()}>

        {step === 'choose' && (
          <>
            <h2 style={{ margin: '0 0 8px', color: 'var(--white)' }}>Delete Pending Leads</h2>
            <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '20px' }}>
              Only pending leads are deleted. Completed leads, in-progress calls, and all results are preserved. Credits are not refunded.
            </p>

            {totalPending === 0 ? (
              <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '20px 0' }}>
                No pending leads to delete.
              </p>
            ) : (
              <>
                <button
                  onClick={() => { setMode('all'); setStep('confirm'); }}
                  className="btn btn-danger"
                  style={{ width: '100%', marginBottom: '12px', padding: '12px' }}
                >
                  Delete All Pending Leads ({totalPending.toLocaleString()})
                </button>

                <div style={{ borderTop: '1px solid var(--dark-3)', margin: '16px 0', paddingTop: '16px' }}>
                  <p style={{ color: 'var(--gray-300)', fontSize: '0.85rem', marginBottom: '12px' }}>Or delete by run:</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(run => {
                      const count = runCounts[run]?.pending || 0;
                      return (
                        <button
                          key={run}
                          disabled={count === 0}
                          onClick={() => { setMode('run'); setSelectedRun(run); setStep('confirm'); }}
                          className="btn btn-secondary"
                          style={{ padding: '8px 4px', fontSize: '0.8rem' }}
                        >
                          <div>Run {run}</div>
                          <div style={{ color: count > 0 ? 'var(--red)' : 'var(--gray-500)', fontSize: '0.75rem' }}>
                            {count.toLocaleString()}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--dark-3)', margin: '16px 0', paddingTop: '16px' }}>
                  <p style={{ color: 'var(--gray-300)', fontSize: '0.85rem', marginBottom: '12px' }}>Or delete by uploaded file:</p>
                  <button
                    onClick={() => { setMode('file'); setStep('pickFile'); loadFileOptions(); }}
                    className="btn btn-primary"
                    style={{ width: '100%', padding: '12px' }}
                  >
                    Delete by uploaded file…
                  </button>
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: '8px', lineHeight: 1.4 }}>
                    Only uploads named when this feature went live can be deleted by file.
                    Older leads are deletable via &ldquo;all&rdquo; or &ldquo;by run&rdquo;.
                    Includes any matching leads still waiting to be verified.
                  </p>
                </div>
              </>
            )}

            <button onClick={handleClose} className="btn btn-secondary" style={{ width: '100%', marginTop: '16px' }}>
              Cancel
            </button>
          </>
        )}

        {step === 'pickFile' && (
          <>
            <h2 style={{ margin: '0 0 8px', color: 'var(--white)' }}>Pick an uploaded file</h2>
            <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '16px' }}>
              Showing pending leads grouped by upload. Already-completed leads aren&rsquo;t shown.
              In-flight verification jobs that haven&rsquo;t finished yet will appear here once
              they&rsquo;re written to your dashboard.
            </p>
            {filesLoading && <p style={{ color: 'var(--gray-400)' }}>Loading…</p>}
            {!filesLoading && fileOptions && fileOptions.length === 0 && (
              <p style={{ color: 'var(--gray-500)' }}>No labelled uploads with pending leads.</p>
            )}
            {!filesLoading && fileOptions && fileOptions.map(opt => (
              <button
                key={opt.sourceFile}
                onClick={() => { setSelectedSourceFile(opt.sourceFile); setStep('confirm'); }}
                className="btn btn-secondary"
                style={{ width: '100%', marginBottom: '8px', textAlign: 'left', padding: '10px 12px' }}
              >
                <div style={{ color: 'var(--white)', fontSize: '0.9rem' }}>{opt.sourceFile}</div>
                <div style={{ color: 'var(--red)', fontSize: '0.8rem' }}>{opt.pendingCount.toLocaleString()} pending</div>
              </button>
            ))}
            {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '12px' }}>{error}</p>}
            <button onClick={() => { setStep('choose'); setMode('all'); setError(null); }} className="btn btn-secondary" style={{ width: '100%', marginTop: '12px' }}>
              Back
            </button>
          </>
        )}

        {step === 'confirm' && (
          <>
            <h2 style={{ margin: '0 0 12px', color: '#dc2626' }}>Confirm Deletion</h2>
            <p style={{ color: 'var(--gray-300)', marginBottom: '16px' }}>
              You are about to delete <strong style={{ color: 'var(--white)' }}>{targetCount.toLocaleString()} pending leads</strong> from <strong style={{ color: 'var(--white)' }}>{targetLabel}</strong>. This cannot be undone.
            </p>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Type DELETE to confirm:</label>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoFocus
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '1rem',
                  textAlign: 'center', letterSpacing: '2px',
                }}
              />
            </div>
            {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => { setStep(mode === 'file' ? 'pickFile' : 'choose'); setConfirmText(''); setError(null); }} className="btn btn-secondary" style={{ flex: 1 }}>
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={confirmText !== 'DELETE'}
                className="btn btn-danger"
                style={{ flex: 1 }}
              >
                Delete {targetCount.toLocaleString()} Leads
              </button>
            </div>
          </>
        )}

        {step === 'progress' && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ color: 'var(--gold)', fontSize: '1.1rem', marginBottom: '8px' }}>Deleting leads...</div>
            <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>
              Removing {targetCount.toLocaleString()} pending leads from {targetLabel}. Please wait.
            </p>
          </div>
        )}

        {step === 'done' && (
          <>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ color: 'var(--green, #10b981)', fontSize: '1.1rem', marginBottom: '8px' }}>
                {result}
              </div>
              <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                The run grid will update automatically.
              </p>
            </div>
            <button onClick={handleClose} className="btn btn-primary" style={{ width: '100%' }}>
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
